/**
 * CartPage component
 * @returns <CartPage />
 */

import { useLocation, Link } from "react-router-dom";
import { useGetCartQuery } from "../redux/apiSlice";
import { AnimatePresence } from "framer-motion";

import Breadcrumbs from "../components/Breadcrumbs";
import TitleAndButton from "../components/TitleAndButton";
import Button from "../components/Button";
import CartAddedItemContainer from "../components/CartAddedItemContainer";
import LegalInformation from "../components/LegalInformation";
import InputContainer from "../components/InputContainer";
import BoxWithBorder from "../components/BoxWithBorder";
import Discount from "../components/Discount";
import Loader from "../components/Loader";

// Utility function to calculate the total price by item type
const getTotalByType = (items) => {
    const total = items.reduce((previousValue, item) => {
        return previousValue + item.totalPrice;
    }, 0);

    return total;
};

const CartPage = () => {
    const location = useLocation();

    // Fetches cart data from the apiSlice
    const {
        data: cart,
        isFetching,
        isLoading,
        isSuccess,
        isError,
    } = useGetCartQuery();

    // Filters cart items by type and calculates their total prices
    const maintenanceItems = isSuccess
        ? cart.items.filter((item) => item.type === "maintenance")
        : "";
    const maintenanceTotal = isSuccess ? getTotalByType(maintenanceItems) : 0;

    const accesoriesItems = isSuccess
        ? cart.items.filter((item) => item.type === "accessory")
        : "";
    const accesoriesTotal = isSuccess ? getTotalByType(accesoriesItems) : 0;

    const merchandisingItems = isSuccess
        ? cart.items.filter((item) => item.type === "merchandise")
        : "";
    const merchandisingTotal = isSuccess
        ? getTotalByType(merchandisingItems)
        : 0;

    // Discounts
    const hasDiscounts = cart?.discount?.percentage > 0 && cart?.discount?.price > 0

    // BreadcrumbsProps define links for the Breadcrumbs component
    const BreadcrumbsProps = {
        links: [
            {
                link: "/",
                label: "Inicio",
            },
            {
                link: location.pathname,
                label: location.pathname.slice(1),
            },
        ],
    };

    // TODO: discount is attached to data.
    // I can remove data when i refactor the discount
    const data = {};

    return (
        <>
            {isLoading && (
                <AnimatePresence>
                    <Loader />
                </AnimatePresence>
            )}
            {isSuccess && !isLoading && (
                <>
                    <Breadcrumbs {...BreadcrumbsProps} />
                    <section className="cart wrapper">
                        {/* Cart empty */}
                        {cart.items.length <= 0 && (
                            <div className="cart__empty">
                                {/* Agregar un condicional para que chequee si existe el array de buttons en TitleAndButton */}
                                <TitleAndButton
                                    title={"Carrito de compras"}
                                    buttons={[]}
                                />

                                <div className="cart__empty__content">
                                    <img
                                        src={
                                            `${window._env_.STATIC_HOST}/static/website/images/carrito.svg`
                                        }
                                        alt=""
                                    />

                                    <p>Aún no agregaste nada a tu carrito</p>

                                    <Button
                                        text="Empezar a comprar"
                                        url="/"
                                        btnStyle="outline"
                                        type="button"
                                        color="black"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Cart with items */}
                        {cart.items.length > 0 && (
                            <>
                                <div className="cart__grid">
                                    <div className="box-1">
                                        <TitleAndButton
                                            title={"Carrito de compras"}
                                            buttons={[
                                                {
                                                    config: {
                                                        tag: "link",
                                                        text: "Seguir comprando",
                                                        color: "black",
                                                        extraClass: "with-icon",
                                                        hasIcon: true,
                                                        url: "/",
                                                    },
                                                },
                                            ]}
                                        />

                                        <CartAddedItemContainer
                                            items={cart.items}
                                            canEdit={true}
                                        />

                                        {data.discount && (
                                            <InputContainer
                                                title={"Promociones"}
                                            >
                                                <Discount
                                                    text={
                                                        "Obtén 10% comprando 2 unidades, 15% comprando 3 unidades o 20% por compras de 4 o más."
                                                    }
                                                />
                                            </InputContainer>
                                        )}
                                    </div>

                                    <div className="box-2">
                                        <InputContainer title={"Resumen"}>
                                            {maintenanceItems &&
                                                maintenanceItems.length > 0 && (
                                                    <BoxWithBorder
                                                        type="cart-totals"
                                                        label={`${maintenanceItems.length
                                                            } ${maintenanceItems.length >
                                                                1
                                                                ? "servicios"
                                                                : "servicio"
                                                            }`}
                                                        price={maintenanceTotal}
                                                        // Maintenances Discount
                                                        discount={hasDiscounts ? cart.discount : null}
                                                    />
                                                )}
                                            {accesoriesItems.length > 0 && (
                                                <BoxWithBorder
                                                    type="cart-totals"
                                                    label={`${accesoriesItems.length
                                                        } ${accesoriesItems.length >
                                                            1
                                                            ? "accesorios"
                                                            : "accesorio"
                                                        }`}
                                                    price={accesoriesTotal}
                                                />
                                            )}
                                            {merchandisingItems.length > 0 && (
                                                <BoxWithBorder
                                                    type="cart-totals"
                                                    label={`${merchandisingItems.length
                                                        } ${merchandisingItems.length >
                                                            1
                                                            ? "productos de merchandising"
                                                            : "producto de merchandising"
                                                        }`}
                                                    price={merchandisingTotal}
                                                />
                                            )}

                                            {/* <Button tag={"link"} text={"¿tienes un código promocional?"} hasIcon={true} /> */}
                                        </InputContainer>

                                        <InputContainer
                                            title={"Total"}
                                            subtitle={
                                                "¡No te pierdas la oportunidad de tenerlo ahora!"
                                            }
                                            extraClass="--cart-total-cta"
                                        >
                                            <Link to="/carrito/checkout">
                                                <Button
                                                    tag={
                                                        "cart-with-total-1-line"
                                                    }
                                                    text={"Realizar pago"}
                                                    hasIcon={true}
                                                    totalPrice={cart.total}
                                                    currency={"CL"}
                                                />
                                            </Link>

                                            {/* <Button {...data.total.btnConfig} onClick={onClickHandler} /> */}
                                        </InputContainer>
                                    </div>
                                </div>
                            </>
                        )}
                    </section>
                    <LegalInformation
                        texts={[
                            "(*) Precios sugeridos.Valores de consumo de combustible han sido obtenidos en mediciones de laboratorio según ciclo de ensayo de la comunidad europea, homologadas en el mtt.Más información: www.Consumovehicular.Cl para ver los términos y condiciones de los modelos y sus versiones, diríjase a los términos y condiciones generales en cada modelo publicado o en la siguiente sección: www.Toyota.Cl / terminos - y - condiciones - comerciales - por - modelo /",
                            "(**) Acceda a precios preferenciales en el valor de las mantenciones a través de su compra online en tienda.Toyota.Cl.Precios preferenciales con rebajas de hasta 20 % sobre el precio referencial comprando 4 o más mantenciones.Ejemplo: valor referencial cuatro mantenciones(10k, 20k, 30k, 40k) rav4 = $.1089.960 valor compra online = $871.968.Precios preferenciales no aplican para otras formas ni canales de pago.Para conocer precios preferenciales por compras online de mantenciones individuales o paquetes, consulte en tienda.Toyota.Cl.El precio de cada mantención prepagada(mpp) incluye iva, repuestos, insumos, mano de obra.No incluyen reparaciones, trabajos ni cambio de piezas fuera del plan de mantenimiento del vehículo.El plan de mantenimiento se encuentra en el manual de garantía y servicio del vehículo.",
                        ]}
                    />
                </>
            )}
            {isError && <p>Hubo un error</p>}
        </>
    );
};

export default CartPage;
